import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { QrCode, Mail, Ban, RefreshCw, Loader2, CheckCircle, MessageSquare } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { QRCodeModal } from '@/components/QRCodeModal';

interface Order {
  id: string;
  order_number: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  client?: { full_name: string; email?: string };
  vendor?: { full_name: string; email?: string; business_email?: string };
  product?: { title: string };
  relay?: { name: string };
  pickup_tokens?: Array<{
    qr_svg: string;
    otp_expires_at: string;
  }>;
}

interface OrderActionsDialogProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

export const OrderActionsDialog = ({ order, isOpen, onClose, onRefresh }: OrderActionsDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrData, setQrData] = useState<{ qr_svg: string; otp_expires_at: string } | null>(null);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(order);
  const [chatAuthorized, setChatAuthorized] = useState(false);
  const [togglingChat, setTogglingChat] = useState(false);

  // Fetch fresh order data when dialog opens
  useEffect(() => {
    const fetchOrderData = async () => {
      if (!isOpen || !order?.id) return;

      try {
        const { data, error } = await supabase
          .from('orders')
          .select(`
            *,
            client:profiles!orders_client_id_fkey(full_name),
            vendor:profiles!orders_vendor_id_fkey(full_name, business_email),
            product:products(title),
            relay:relays(name),
            pickup_tokens(qr_svg, otp_expires_at)
          `)
          .eq('id', order.id)
          .single();

        if (error) throw error;
        setCurrentOrder(data as any);
        setChatAuthorized((data as any)?.chat_authorized ?? false);
      } catch (error) {
        console.error('Error fetching order:', error);
      }
    };

    fetchOrderData();
  }, [isOpen, order?.id]);

  if (!currentOrder) return null;

  const handleRegenerateQR = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('regenerate-qr-code', {
        body: { orderId: currentOrder.id }
      });

      if (error) throw error;

      toast({
        title: "QR Code régénéré et envoyé",
        description: "Le nouveau QR code a été créé et envoyé par email au client",
      });

      setQrData({
        qr_svg: data.token.qr_svg,
        otp_expires_at: data.token.expires_at
      });
      setShowQRModal(true);
      onRefresh();
    } catch (error: any) {
      console.error('Error regenerating QR:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de régénérer le QR code",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewQR = () => {
    if (currentOrder.pickup_tokens && currentOrder.pickup_tokens[0]) {
      setQrData({
        qr_svg: currentOrder.pickup_tokens[0].qr_svg,
        otp_expires_at: currentOrder.pickup_tokens[0].otp_expires_at
      });
      setShowQRModal(true);
    }
  };

  const handleSendEmailToVendor = async () => {
    if (!emailMessage.trim()) {
      toast({
        title: "Message requis",
        description: "Veuillez entrer un message",
        variant: "destructive",
      });
      return;
    }

    const vendorEmail = currentOrder.vendor?.business_email || currentOrder.vendor?.email;
    if (!vendorEmail) {
      toast({
        title: "Erreur",
        description: "Le vendeur n'a pas d'email configuré",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('send-vendor-notification', {
        body: {
          vendorEmail,
          vendorName: currentOrder.vendor?.full_name,
          subject: `Notification concernant la commande ${currentOrder.order_number}`,
          message: emailMessage,
          orderId: currentOrder.id,
          orderNumber: currentOrder.order_number
        }
      });

      if (error) throw error;

      toast({
        title: "Email envoyé",
        description: `Email envoyé à ${currentOrder.vendor?.full_name}`,
      });
      setEmailMessage('');
    } catch (error: any) {
      console.error('Error sending email:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'envoyer l'email",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!confirm('Êtes-vous sûr de vouloir confirmer manuellement ce paiement ?')) return;

    try {
      setLoading(true);
      
      // Update order status to PAID with timestamp
      const { error: updateError } = await supabase
        .from('orders')
        .update({ 
          status: 'PAID' as any,
          paid_at: new Date().toISOString()
        })
        .eq('id', currentOrder.id);

      if (updateError) throw updateError;

      // Generate pickup token
      const { error: tokenError } = await supabase.functions.invoke('generate-pickup-token', {
        body: { orderId: currentOrder.id }
      });

      if (tokenError) {
        console.error('Warning: Failed to generate pickup token:', tokenError);
        toast({
          title: "Paiement confirmé avec avertissement",
          description: "Le paiement a été confirmé mais le code de retrait n'a pas pu être généré",
        });
      } else {
        toast({
          title: "Paiement confirmé",
          description: "Le paiement a été confirmé manuellement avec succès",
        });
      }
      
      onRefresh();
      onClose();
    } catch (error: any) {
      console.error('Error confirming payment:', error);
      toast({
        title: "Erreur",
        description: "Impossible de confirmer le paiement",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!confirm('Êtes-vous sûr de vouloir annuler cette commande ?')) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('orders')
        .update({ status: 'CANCELLED' as any })
        .eq('id', currentOrder.id);

      if (error) throw error;

      toast({
        title: "Commande annulée",
        description: "La commande a été annulée avec succès",
      });
      
      onRefresh();
      onClose();
    } catch (error: any) {
      console.error('Error cancelling order:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'annuler la commande",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChatAuthorization = async () => {
    try {
      setTogglingChat(true);
      const newValue = !chatAuthorized;

      const { error } = await supabase
        .from('orders')
        .update({ chat_authorized: newValue } as any)
        .eq('id', currentOrder.id);

      if (error) throw error;

      setChatAuthorized(newValue);
      toast({
        title: newValue ? 'Chat autorisé' : 'Chat désactivé',
        description: newValue
          ? 'Le chat entre le client et le vendeur est maintenant ouvert'
          : 'Le chat entre le client et le vendeur est maintenant fermé',
      });
    } catch (error: any) {
      console.error('Error toggling chat:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier l\'autorisation du chat',
        variant: 'destructive',
      });
    } finally {
      setTogglingChat(false);
    }
  };

  const hasQRCode = currentOrder.pickup_tokens && currentOrder.pickup_tokens.length > 0;
  const canRegenerateQR = ['PAID', 'AVAILABLE_FOR_PICKUP'].includes(currentOrder.status);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Actions sur la commande #{currentOrder.order_number}</DialogTitle>
            <DialogDescription>
              {currentOrder.product?.title} - {currentOrder.amount_cents / 100} {currentOrder.currency}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Order Info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Client</p>
                <p className="font-medium">{currentOrder.client?.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Vendeur</p>
                <p className="font-medium">{currentOrder.vendor?.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Point relais</p>
                <p className="font-medium">{currentOrder.relay?.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Statut</p>
                <p className="font-medium">{currentOrder.status}</p>
              </div>
            </div>

            {/* Chat Authorization Toggle */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Chat acheteur / vendeur
              </h3>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="text-sm font-medium">
                    {chatAuthorized ? 'Chat autorisé' : 'Chat non autorisé'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {chatAuthorized
                      ? 'Le client et le vendeur peuvent échanger des messages'
                      : 'Activez pour permettre la communication'
                    }
                  </p>
                </div>
                <Switch
                  checked={chatAuthorized}
                  onCheckedChange={handleToggleChatAuthorization}
                  disabled={togglingChat}
                />
              </div>
            </div>

            {/* QR Code Actions */}
            <div className="space-y-3">
              <h3 className="font-semibold">Code QR de retrait</h3>
              <div className="flex gap-2">
                {hasQRCode && (
                  <Button
                    onClick={handleViewQR}
                    variant="outline"
                    className="flex-1"
                  >
                    <QrCode className="h-4 w-4 mr-2" />
                    Afficher le QR Code
                  </Button>
                )}
                {canRegenerateQR && (
                  <Button
                    onClick={handleRegenerateQR}
                    variant="outline"
                    disabled={loading}
                    className="flex-1"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Régénérer le QR Code
                  </Button>
                )}
              </div>
            </div>

            {/* Email Vendor */}
            <div className="space-y-3">
              <h3 className="font-semibold">Contacter le vendeur</h3>
              <div className="space-y-2">
                <Label htmlFor="email-message">Message</Label>
                <Textarea
                  id="email-message"
                  placeholder="Écrivez votre message au vendeur..."
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={4}
                />
              </div>
              <Button
                onClick={handleSendEmailToVendor}
                disabled={loading || !emailMessage.trim()}
                className="w-full"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Envoyer un email au vendeur
              </Button>
            </div>

            {/* Confirm Payment */}
            {currentOrder.status === 'PENDING_PAYMENT' && (
              <div className="space-y-3 pt-4 border-t">
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-900">
                    <strong>Confirmation manuelle :</strong> Utilisez cette action uniquement si Stripe n'a pas confirmé le paiement automatiquement.
                  </p>
                </div>
                <Button
                  onClick={handleConfirmPayment}
                  disabled={loading}
                  variant="default"
                  className="w-full"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Confirmer le paiement manuellement
                </Button>
              </div>
            )}

            {/* Cancel Order */}
            {currentOrder.status !== 'CANCELLED' && currentOrder.status !== 'PICKED_UP' && (
              <div className="space-y-3 pt-4 border-t">
                <Button
                  onClick={handleCancelOrder}
                  disabled={loading}
                  variant="destructive"
                  className="w-full"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Ban className="h-4 w-4 mr-2" />
                  )}
                  Annuler la commande
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {qrData && (
        <QRCodeModal
          isOpen={showQRModal}
          onClose={() => setShowQRModal(false)}
          qrSvg={qrData.qr_svg}
          orderTitle={currentOrder.product?.title || ''}
          expiresAt={qrData.otp_expires_at}
        />
      )}
    </>
  );
};
